import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';

import { useCreateProductFamily, useUpdateProductFamily } from '../../hooks';
import type { AttributeDefinition, CatalogLanguage, ProductFamily } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { LocalizedFieldsEditor } from './LocalizedFieldsEditor';
import type { LocalizedFieldValue } from './LocalizedFieldsEditor';
import { ProductFamilyAttributesEditor } from './ProductFamilyAttributesEditor';

interface ProductFamilyFormDialogProps {
  open: boolean;
  /** The family being edited (full detail — the list endpoint already returns it), or `null` to create. */
  editing: ProductFamily | null;
  languages: CatalogLanguage[];
  dictionary: AttributeDefinition[];
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Create / edit a product family (attribute template) — user-story-catalog-005: identity + name
 * translations, and — in edit mode, once the family code exists — the attribute attach editor
 * (required / variant axis / sort order).
 */
export function ProductFamilyFormDialog({
  open,
  editing,
  languages,
  dictionary,
  canManage,
  onClose,
  onResult,
}: ProductFamilyFormDialogProps) {
  const { t } = useTranslation('catalog');
  const create = useCreateProductFamily();
  const update = useUpdateProductFamily();
  const isEdit = editing !== null;
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);
  const [nameValues, setNameValues] = useState<LocalizedFieldValue[]>([]);

  useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? '');
    setActive(editing?.active ?? true);
    setNameValues(
      (editing?.translations ?? []).map((translation) => ({
        locale: translation.locale,
        text: translation.name,
      })),
    );
  }, [open, editing]);

  const defaultLanguage = useMemo(() => languages.find((l) => l.defaultLocale), [languages]);
  const defaultNameFilled =
    !defaultLanguage ||
    nameValues.some((v) => v.locale === defaultLanguage.locale && v.text.trim() !== '');
  const valid = code.trim() !== '' && defaultNameFilled;

  const submit = () => {
    if (!valid) return;
    const translations = nameValues.map((v) => ({ locale: v.locale, name: v.text }));
    const handlers = {
      onSuccess: () => {
        onResult('success', isEdit ? t('productFamilyFormDialog.saved') : t('productFamilyFormDialog.created'));
        onClose();
      },
      onError: (error: NormalizedApiError) => onResult('error', error.message),
    };
    if (isEdit && editing) {
      update.mutate(
        { code: editing.code, body: { version: editing.version, active, translations } },
        handlers,
      );
      return;
    }
    create.mutate({ code: code.trim(), translations }, handlers);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {isEdit ? t('productFamilyFormDialog.editTitle') : t('productFamilyFormDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label={t('productFamilyFormDialog.codeLabel')}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={code.trim() === ''}
              disabled={isEdit || !canManage}
              autoFocus={!isEdit}
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
            />
            {isEdit ? (
              <FormControlLabel
                control={
                  <Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!canManage} />
                }
                label={t('productFamilyFormDialog.activeLabel')}
                sx={{ flexShrink: 0 }}
              />
            ) : null}
          </Stack>

          <LocalizedFieldsEditor
            languages={languages}
            values={nameValues}
            onChange={setNameValues}
            fieldLabel={t('productFamilyFormDialog.nameFieldLabel')}
            disabled={!canManage}
          />

          <Divider />

          {isEdit && editing ? (
            <ProductFamilyAttributesEditor
              familyCode={editing.code}
              attached={editing.attributes}
              dictionary={dictionary}
              canManage={canManage}
              onNotify={onResult}
            />
          ) : (
            <Alert severity="info">{t('productFamilyFormDialog.attributesAfterCreateHint')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending || !canManage}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
